import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { liftListItem } from "@tiptap/pm/schema-list";
import { EditorState, Plugin, TextSelection } from "@tiptap/pm/state";
import { canSplit, Step } from "@tiptap/pm/transform";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import LinkExtension from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import {
  HtmlToMarkdownOptions,
  processEditorContentForSave,
  processStoredContentForEditor
} from "./markdownConverter";
import { TEXT_EDITOR_STYLING_CLASSES } from "./textEditorStyles";
import { TextEditorFormat } from "./restrictedMarkdown";
import { MaxLength } from "./maxLength";
import { TextShortcuts } from "@/utils/textShortcuts/tiptapTextShortcuts";

interface EditorExtensionOptions {
  // Only these formats; keyboard shortcuts and pasted content can't add others.
  formats?: TextEditorFormat[];
  maxLength?: number;
}

const isList = (node: ProseMirrorNode) =>
  node.type.name === "bulletList" || node.type.name === "orderedList";

// A list item always opens with a paragraph ("paragraph block*"), so text sits
// three opens into a list (list, item, paragraph) and two into an item.
const INTO_LIST_TEXT = 3;
const INTO_ITEM_TEXT = 2;

// One flattening move, as steps: lift a nested list, or split an item's extra block off.
const flattenSteps = (doc: ProseMirrorNode): readonly Step[] => {
  let found: { node: ProseMirrorNode; pos: number } | undefined;
  doc.descendants((node, pos, parent, index) => {
    // Keep the last: nothing follows it in its item, which a lift needs.
    if (parent?.type.name === "listItem" && index > 0) found = { node, pos };
  });
  if (!found) return [];
  const { node, pos } = found;
  if (!isList(node)) {
    return canSplit(doc, pos, 1)
      ? EditorState.create({ doc }).tr.split(pos, 1).steps
      : [];
  }
  // From inside the nested list's first item to inside its last.
  const lastItemPos = pos + node.nodeSize - 1 - (node.lastChild?.nodeSize ?? 0);
  const state = EditorState.create({
    doc,
    selection: TextSelection.create(
      doc,
      pos + INTO_LIST_TEXT,
      lastItemPos + INTO_ITEM_TEXT
    )
  });
  let lifted: readonly Step[] = [];
  liftListItem(doc.type.schema.nodes.listItem)(state, (tr) => {
    lifted = tr.steps;
  });
  return lifted;
};

// One list level and one paragraph per item, after any change: toggle, paste or load.
const flatListsPlugin = () =>
  new Plugin({
    appendTransaction: (transactions, _oldState, newState) => {
      if (!transactions.some((transaction) => transaction.docChanged)) {
        return null;
      }
      const tr = newState.tr;
      for (
        let steps = flattenSteps(tr.doc);
        steps.length;
        steps = flattenSteps(tr.doc)
      ) {
        steps.forEach((step) => tr.step(step));
      }
      if (!tr.docChanged) return null;
      // Flattening a value set from outside isn't an edit by the user.
      if (
        transactions.every((transaction) =>
          transaction.getMeta("preventUpdate")
        )
      ) {
        tr.setMeta("preventUpdate", true).setMeta("addToHistory", false);
      }
      return tr;
    }
  });

// Lists stay flat: Tab and Shift-Tab are unbound, so Tab moves focus on.
const FlatListItem = ListItem.extend({
  addKeyboardShortcuts() {
    return {
      // A range is deleted first; outside a list, Enter falls through to the default split.
      Enter: () => {
        if (!this.editor.state.selection.empty) {
          this.editor.commands.deleteSelection();
        }
        return this.editor.commands.splitListItem(this.name);
      }
    };
  },
  addProseMirrorPlugins() {
    return [flatListsPlugin()];
  }
});

// StarterKit cut down to the given formats, with lists one level deep.
const restrictedStarterKit = (formats: TextEditorFormat[]) => {
  const allows = (format: TextEditorFormat) => formats.includes(format);
  const lists = allows("bulletList") || allows("orderedList");
  return [
    StarterKit.configure({
      blockquote: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      strike: false,
      listItem: false,
      ...(allows("bold") ? {} : { bold: false }),
      ...(allows("italic") ? {} : { italic: false }),
      ...(allows("bulletList") ? {} : { bulletList: false }),
      ...(allows("orderedList") ? {} : { orderedList: false })
    }),
    ...(lists ? [FlatListItem] : [])
  ];
};

export const createEditorExtensions = (
  placeholder?: string,
  { formats, maxLength }: EditorExtensionOptions = {}
) => {
  const placeholderExtension = Placeholder.configure({
    placeholder: placeholder || "",
    emptyEditorClass: "is-editor-empty"
  });
  const limit =
    maxLength !== undefined ? [MaxLength.configure({ limit: maxLength })] : [];
  if (formats) {
    // No arrow shortcuts: the narrower renderer may not have the glyphs.
    return [...restrictedStarterKit(formats), placeholderExtension, ...limit];
  }
  return [
    StarterKit,
    Heading.configure({
      levels: [1, 2, 3]
    }),
    LinkExtension.configure({
      openOnClick: true,
      HTMLAttributes: {
        class: "text-link hover:text-link-hover cursor-pointer underline"
      }
    }),
    placeholderExtension,
    // Auto-replace arrow shortcuts (-> → →, <-> → ↔). See utils/textShortcuts.
    TextShortcuts,
    ...limit
  ];
};

export const createEditorProps = () => ({
  attributes: {
    class: [
      "focus:outline-none pl-3 pr-2 py-[6px] text-primary",
      ...TEXT_EDITOR_STYLING_CLASSES
    ].join(" ")
  }
});

export const createParseOptions = () => ({
  preserveWhitespace: "full" as const
});

// Legacy function for backward compatibility - now converts HTML to markdown for secure storage
export const processEditorContent = (
  html: string,
  options?: HtmlToMarkdownOptions
) => {
  return processEditorContentForSave(html, options);
};

// New function to prepare stored content for loading into the editor
export const processStoredContent = (content: string | undefined) => {
  return processStoredContentForEditor(content || "");
};
